import React from 'react';
import * as Sentry from '@sentry/browser';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  DocumentNode,
} from '@apollo/client';
import {
  classifyReplayError,
  OfflineMutationLoader,
  Rehydrated,
  TrackedQuery,
} from '@bilderit/offline-link';

import { useCurrentIdentity } from '@/utils/currentIdentity';
import fragmentTypes from '@/generated/fragmentTypes.json';
import { typeDefs } from '@/utils/clientSchema';
import { stringify } from '@/utils/stringify';
import { localFields } from './localFields';
import { localQueries } from './localQueries';
import { links as link } from './links';
import { registerApolloCache } from './errorLink';
import { createFragmentRegistry } from '@apollo/client/cache';
import fragments from '@/generated/fragments.json';
import { toast } from 'sonner';
import { LoadingMessageCard } from '@/components/LoadingMessageCard';
import { BilderitLogoHorizontal } from '@/components/BilderitLogoHorizontal';
import merge from 'lodash/merge';
import { scalarTypePolicies } from '@/generated/scalarTypePolicies';

const cache = new InMemoryCache({
  possibleTypes: fragmentTypes.possibleTypes,
  typePolicies: merge(
    localFields,
    {
      Query: localQueries,
    },
    scalarTypePolicies
  ),
  fragments: createFragmentRegistry(fragments as DocumentNode),
});

// errorLink evicts cache fields the server denies (NOT_AUTHORIZED), but
// Apollo doesn't expose the cache to links, and errorLink can't import it
// from this module (this module imports the links — that would be a
// cycle), so we hand the cache over once, right here, before the client
// is built.
registerApolloCache(cache);

export const client = new ApolloClient({
  link,
  cache,
  connectToDevTools:
    import.meta.env.MODE === 'development' ||
    import.meta.env.VITE_APP_SENTRY_ENVIRONMENT !== 'prod',
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    query: {
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    },
  },
  typeDefs,
});

const WithProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const errorHandler = React.useCallback(
    (err: unknown, trackedQueries: TrackedQuery[]) => {
      const classification = classifyReplayError(err);
      const isDeadLettered = classification.kind === 'permanent';
      // Permanent rejections are a business decision by the server (e.g. an
      // authz denial) — show the real message so the operator understands
      // why. Transient failures (network, internal server error) keep the
      // generic toast: the entry is still queued and will retry.
      if (isDeadLettered) {
        toast.error(
          `No se pudo enviar un parte guardado sin conexión: ${classification.message}`,
          { duration: 5000 }
        );
      } else {
        toast.error(
          'Parece que hubo un error al cargar uno de tus partes. Por favor revisa tus partes cargados y volve a cargarlo si fuera necesario.',
          { duration: 5000 }
        );
      }
      Sentry.withScope((scope) => {
        scope.setTag('AppCrashed', 'false');
        scope.setTag('offlineMutationError', 'true');
        scope.setTag(
          'offlineMutationDeadLettered',
          isDeadLettered ? 'true' : 'false'
        );
        Sentry.captureException(err, {
          contexts: {
            offlineLink: { trackedQueries: trackedQueries.map(stringify(2)) },
          },
        });
      });
    },
    []
  );
  const identity = useCurrentIdentity();
  const fallback = (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-sidebar p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <a href="/" className="flex items-center gap-2 self-center font-medium">
          <BilderitLogoHorizontal className="h-12" />
        </a>
        <LoadingMessageCard message="Cargando cache..." />
      </div>
    </div>
  );
  return (
    <ApolloProvider client={client}>
      {identity === undefined ? (
        fallback
      ) : (
        <Rehydrated identity={identity} fallback={fallback}>
          <OfflineMutationLoader owner={identity} errorHandler={errorHandler} />
          {children}
        </Rehydrated>
      )}
    </ApolloProvider>
  );
};

export default WithProvider;
